/**
 * @file Contextual `E` interaction (GDD §Fase 3 — weapon swap).
 *
 * Owns two things and nothing else: which interactable the player is close
 * enough to act on, and what pressing the key does to it. Where the prompt is
 * drawn is the HUD's problem — this system only says which entity is offering
 * one, and the payload carries a live position for the HUD to project.
 *
 * Swapping is deliberately a key press rather than a vacuum: picking up a gun
 * costs you the one you are holding, so it has to be a decision.
 */

import { System } from '../ecs/System.js';
import {
  Health,
  InteractionTarget,
  Pickup,
  PlayerControlled,
  Transform,
  Weapon,
  Chest
} from '../components/index.js';
import { dropWeapon } from '../entities/WeaponDrop.js';
import { pickupDefinition, rollWeaponPowerup } from '../config/Powerups.js';
import { WEAPONS } from '../config/Weapons.js';
import { Layer } from '../config/Layers.js';
import { Action } from '../input/InputManager.js';
import { Events } from '../core/EventBus.js';
import { applyPlayerAmmoPolicy } from '../combat/ammoPolicy.js';

export class InteractionSystem extends System {
  /** After `PickupSystem` (55), so the prompt tracks this frame's bob. */
  static priority = 57;

  constructor() {
    super();
    /**
     * Reused prompt payload: this runs every frame, so it must not allocate.
     * @type {{ visible: boolean, prompt: string, label: string,
     *          position: import('three').Vector3 | null }}
     * @private
     */
    this._prompt = { visible: false, prompt: 'E', label: '', position: null };
  }

  /**
   * @param {number} _dt Delta time in seconds.
   * @returns {void}
   */
  update(_dt) {
    const player = this._findPlayer();
    const target = player ? this._findNearest(player) : null;

    if (target && this.ctx.input.justPressed(Action.INTERACT)) {
      if (target.has(Pickup)) this._swapWeapon(player, target);
      else if (target.has(Chest)) this._openChest(player, target);
      // The pickup is gone: retract the prompt now rather than a frame late.
      this._publish(null);
      return;
    }

    this._publish(target);
  }

  /**
   * @returns {import('../ecs/Entity.js').Entity | null} The living player.
   * @private
   */
  _findPlayer() {
    for (const player of this.world.query(PlayerControlled, Transform, Health)) {
      if (!player.get(Health).dead) return player;
    }
    return null;
  }

  /**
   * Closest interactable in range, so a pile of drops still resolves to exactly
   * one — and always the same one.
   *
   * @param {import('../ecs/Entity.js').Entity} player Interacting player.
   * @returns {import('../ecs/Entity.js').Entity | null}
   * @private
   */
  _findNearest(player) {
    const origin = player.get(Transform).position;
    const radius = this.ctx.config.interaction.radius;

    let best = null;
    let bestDistanceSq = radius * radius;

    for (const entity of this.world.query(InteractionTarget, Transform)) {
      const position = entity.get(Transform).position;
      const dx = position.x - origin.x;
      const dz = position.z - origin.z;
      const distanceSq = dx * dx + dz * dz;

      if (distanceSq > bestDistanceSq) continue;
      best = entity;
      bestDistanceSq = distanceSq;
    }

    return best;
  }

  /**
   * @param {import('../ecs/Entity.js').Entity | null} target Entity offering a
   *   prompt, or null when nothing is in range.
   * @returns {void}
   * @private
   */
  _publish(target) {
    const prompt = this._prompt;

    if (!target) {
      // Still emitted once the target is gone: the HUD has to be told to hide.
      if (!prompt.visible) return;
      prompt.visible = false;
      prompt.position = null;
      this.ctx.events.emit(Events.INTERACTION_PROMPT, prompt);
      return;
    }

    const interaction = target.get(InteractionTarget);
    prompt.visible = true;
    prompt.prompt = interaction.prompt;
    prompt.label = interaction.label;
    prompt.position = target.get(Transform).position;
    this.ctx.events.emit(Events.INTERACTION_PROMPT, prompt);
  }

  /**
   * Trades the player's gun for the one on the floor: the old stat block goes
   * back on the ground where the player is standing, the new one goes on the
   * player, and nothing is duplicated or rebuilt in between.
   *
   * @param {import('../ecs/Entity.js').Entity} player Interacting player.
   * @param {import('../ecs/Entity.js').Entity} entity Weapon pickup.
   * @returns {void}
   * @private
   */
  _swapWeapon(player, entity) {
    const pickup = entity.get(Pickup);
    const incoming = pickup.weapon;
    if (!incoming) return;

    const equipped = this._equipIncomingWeapon(player, incoming);
    if (!equipped) return;

    this.ctx.events.emit(Events.PICKUP_COLLECTED, {
      entity,
      key: pickup.key,
      definition: pickup.definition
    });
    this.world.destroyEntity(entity);
  }

  /**
   * @param {import('../ecs/Entity.js').Entity} player Interacting player.
   * @param {import('../ecs/Entity.js').Entity} entity Chest interaction entity.
   * @returns {void}
   * @private
   */
  _openChest(player, entity) {
    const chest = entity.get(Chest);
    if (!chest || chest.opened) return;
    chest.opened = true;
    this.ctx.events.emit(Events.CHEST_OPENED, { index: chest.index });

    const held = player.get(Weapon);
    const grantsWeapon = !held || Math.random() < chest.weaponChance;

    if (grantsWeapon) {
      const key = rollWeaponPowerup();
      const definition = pickupDefinition(key);
      if (definition?.weapon) {
        const weapon = Weapon.fromArchetype(definition.weapon);
        this._equipIncomingWeapon(player, weapon);
      }
    } else if (held && !held.infiniteAmmo) {
      // Tops the magazine and stocks the reserve pool with full magazines'
      // worth of rounds, per the chest's `ammoMagazines` reward.
      held.currentAmmo = held.magazineCapacity;
      if (!held.infiniteReserve) {
        held.reserveAmmo += chest.ammoMagazines * held.magazineCapacity;
      }
      this.ctx.events.emit(Events.AMMO_CHANGED, {
        weapon: held,
        current: held.currentAmmo,
        capacity: held.magazineCapacity,
        reserve: held.reserveAmmo
      });
      this.ctx.events.emit(Events.NOTIFY_ADD, {
        text: 'Ammo refilled',
        type: 'pickup'
      });
    }

    this.world.destroyEntity(entity);
  }

  /**
   * Equips an incoming weapon, dropping the current one when it has a floor
   * representation. Enemy-carried variants are converted to player ammo/layers.
   *
   * @param {import('../ecs/Entity.js').Entity} player Weapon receiver.
   * @param {import('../components/Weapon.js').Weapon} incoming Weapon to equip.
   * @returns {import('../components/Weapon.js').Weapon | null}
   * @private
   */
  _equipIncomingWeapon(player, incoming) {
    releaseHeldWeapon(player, this.ctx);

    // A dropped `*_enemy` variant fires on the enemy layer AND carries the
    // enemy's rebalanced stats and infinite ammo. Handing that to the player
    // would arm them with a wrong-layer, wrong-balance gun, so rebuild it as the
    // player's own version of the same weapon (correct layer, player numbers, a
    // real magazine). Enemy-only guns with no player base — the Spitter — have
    // no `*_enemy` suffix, so they fall through untouched.
    let equipped = incoming;
    const base = incoming.key.replace(/_enemy$/, '');
    if (base !== incoming.key && WEAPONS[base]) {
      equipped = Weapon.fromArchetype(base);
    } else if (
      incoming.infiniteAmmo ||
      incoming.infiniteReserve ||
      incoming.projectileLayer === Layer.ENEMY_BULLET
    ) {
      equipped = Weapon.fromArchetype(incoming.key);
    }

    // An enemy's gun fires on the enemy bullet layer. Ownership decides who the
    // rounds belong to, so retarget on equip rather than storing it per drop.
    // (Player archetypes already declare these, so this is a no-op after a
    // rebuild and the real fix for a passthrough enemy-only gun.)
    equipped.projectileLayer = Layer.PLAYER_BULLET;
    equipped.projectileMask = Layer.WALL | Layer.ENEMY;
    equipped.triggerHeld = false;
    applyPlayerAmmoPolicy(equipped, this.ctx.config?.ammo?.emptyMagazinePolicy);
    player.add(equipped.equip(player, { events: this.ctx.events }));
    this.ctx.playerWeaponKey = equipped.key;

    this.ctx.events.emit(Events.WEAPON_CHANGED, { weapon: equipped });
    return equipped;
  }
}

/**
 * Takes whatever the player is holding out of their hands, in the one way both
 * swap paths agree on: a pickup-able gun is left on the floor where they stood,
 * an undroppable one (the fallback knife, issue #122) simply leaves — dropping it
 * would litter the level with a weapon they are handed again the moment they are
 * empty.
 *
 * Empty hands are the no-op case, which is what lets the fallback path call this
 * unconditionally. Also used by the `unequip_empty` ammo policy.
 *
 * @param {import('../ecs/Entity.js').Entity} player Weapon holder.
 * @param {import('../core/Context.js').GameContext} ctx Shared game context.
 * @returns {void}
 */
export function releaseHeldWeaponForPolicy(player, ctx) {
  releaseHeldWeapon(player, ctx);
}

/**
 * @param {import('../ecs/Entity.js').Entity} player Weapon holder.
 * @param {import('../core/Context.js').GameContext} ctx Shared game context.
 * @returns {void}
 */
function releaseHeldWeapon(player, ctx) {
  const outgoing = player.get(Weapon);
  if (!outgoing) return;

  // An enemy-only gun with no pickup row of its own is neither dropped nor
  // unequipped: whatever is coming in simply replaces it in place.
  const dropsToFloor = Boolean(pickupDefinition(outgoing.key));
  if (outgoing.droppable && !dropsToFloor) return;

  const position = player.get(Transform).position;
  player.remove(Weapon);
  // Cancel any reload in flight so a dropped gun cannot finish loading in
  // someone else's hands, per the weapon framework's contract.
  outgoing.unequip();
  if (!outgoing.droppable) return;

  // Cooldown rides along, so dropping and re-taking a railgun is not a way to
  // skip its recharge. Uses the real weapon model (issue #97), same as an enemy
  // corpse or a stunned disarm.
  dropWeapon(ctx, position.x, position.z, outgoing);
}

/**
 * Equips a weapon on the player by archetype key, without needing a floor
 * pickup entity.  Used by the debug menu; the in-game pickup path calls
 * {@link InteractionSystem#_swapWeapon} instead.
 *
 * @param {import('../ecs/Entity.js').Entity} player The player entity.
 * @param {string} key Archetype key from {@link WEAPONS}.
 * @param {import('../core/Context.js').GameContext} ctx Shared game context.
 * @returns {import('../components/Weapon.js').Weapon | null} The equipped weapon, or null if the key is invalid.
 */
export function equipWeaponOn(player, key, ctx) {
  if (!player) return null;
  const archetype = WEAPONS[key];
  if (!archetype) return null;

  const incoming = Weapon.fromArchetype(key);

  releaseHeldWeapon(player, ctx);

  // Player guns always fire on the player bullet layer.
  incoming.projectileLayer = Layer.PLAYER_BULLET;
  incoming.projectileMask = Layer.WALL | Layer.ENEMY;
  incoming.triggerHeld = false;
  applyPlayerAmmoPolicy(incoming, ctx.config?.ammo?.emptyMagazinePolicy);
  player.add(incoming.equip(player, { events: ctx.events }));
  ctx.playerWeaponKey = incoming.key;

  ctx.events.emit(Events.WEAPON_CHANGED, { weapon: incoming });
  return incoming;
}
